/**
 * Where signing in may send somebody.
 *
 * A workspace that cannot offer Google itself sends people here with `next`.
 * Only a page of ours is honoured: never another origin, never the API, and
 * nothing a crafted link could aim somebody at. This is the same rule the
 * account service applies to a Google return path (portalDestination in
 * packages/hosted/src/google-signin.ts), and the two must stay in step.
 */
export const DEFAULT_DESTINATION = '/workspaces';

/** The workspace list, one workspace, the step that opens one, and support. */
const DESTINATIONS = /^\/(?:workspaces(?:\/[A-Za-z0-9_-]{1,64}(?:\/open)?)?|support(?:\/[A-Za-z0-9_-]{1,64})?)$/;

/**
 * The page `next` names, or the workspace list. The path is judged after the
 * browser's own resolution of it: '/.//evil.com' reads as a path but resolves
 * to '//evil.com', another host, and '/./api/x' resolves to the API.
 */
export function signInDestination(next: string | null | undefined, origin: string): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || /[\\\x00-\x20]/.test(next)) return DEFAULT_DESTINATION;
  let parsed: URL;
  try { parsed = new URL(next, origin); } catch { return DEFAULT_DESTINATION; }
  return parsed.origin === new URL(origin).origin && DESTINATIONS.test(parsed.pathname) ? parsed.pathname : DEFAULT_DESTINATION;
}

/** `?next=…` to carry a real destination on to another sign-in page, or nothing. */
export function nextQuery(next: string | null | undefined, origin: string): string {
  const destination = signInDestination(next, origin);
  return destination === DEFAULT_DESTINATION ? '' : `?next=${encodeURIComponent(destination)}`;
}

export interface DestinationStore { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void }
const STORE_KEY = 'obelisk.after-sign-in';
/** As long as the emailed link it waits for. */
const REMEMBER_MS = 30 * 60_000;

/**
 * Keep a destination across the email round trip. A signup or reset link
 * arrives by email without `next`, so without this a person invited to a
 * workspace finished creating their account somewhere else.
 */
export function rememberDestination(store: DestinationStore | null, destination: string, now = Date.now()) {
  try {
    if (destination === DEFAULT_DESTINATION) store?.removeItem(STORE_KEY);
    else store?.setItem(STORE_KEY, JSON.stringify({ destination, until: now + REMEMBER_MS }));
  } catch { /* Remembering is a convenience. */ }
}

/** The remembered destination, judged again and then forgotten. */
export function recallDestination(store: DestinationStore | null, origin: string, now = Date.now()): string {
  try {
    const raw = store?.getItem(STORE_KEY); store?.removeItem(STORE_KEY);
    if (!raw) return DEFAULT_DESTINATION;
    const saved = JSON.parse(raw) as { destination?: unknown; until?: unknown };
    if (typeof saved.destination !== 'string' || typeof saved.until !== 'number' || saved.until <= now) return DEFAULT_DESTINATION;
    return signInDestination(saved.destination, origin);
  } catch { return DEFAULT_DESTINATION; }
}
